import '../../assets/styles/auth.scss';
import logo from '../../assets/images/logo/logo2.png';
import { Link, useNavigate } from 'react-router-dom';
import henceforthApi from '../../utils/henceforthApi';
import loginSuccess from '../../context/actions/auth/loginSuccess';
import { Fragment, useContext, useState, useRef, useEffect } from 'react';
import { GlobalContext, handleError } from '../../context/Provider';
import { strongPasswordValidation, validateEmail } from '../../utils/validations';
import Spinner from '../../components/common/spinner';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { requestNotification } from '../../utils/firebase';
import VerifyOtp from '../../components/modals/VerifyEmailOtp';
import henceofrthEnums from '../../utils/henceofrthEnums';
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Building2,
  MapPin,
  FileText,
  UploadCloud,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  AlertCircle,
  Store,
  Check,
  X,
  Sparkles
} from 'lucide-react';

interface DocumentType {
  type: string;
  front_image: File | null;
  back_image: File | null;
}

interface DocumentConfig {
  type: string;
  label: string;
}

const Signup = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const { authDispatch, toastMessage, loading, setLoading } = useContext(GlobalContext);

  // Step management
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1 errors
  const [nameErr, setNameErr] = useState('');
  const [emailErr, setEmailErr] = useState('');
  const [phoneErr, setPhoneErr] = useState('');
  const [passwordErr, setPasswordErr] = useState('');

  // Step 2 errors
  const [companyErr, setCompanyErr] = useState('');
  const [addressErr, setAddressErr] = useState('');

  // Step 3 errors
  const [frontImageErr, setFrontImageErr] = useState('');
  const [backImageErr, setBackImageErr] = useState('');
  const [checkboxErr, setCheckboxErr] = useState('');
  const [checkbox, setCheckbox] = useState(false);
  const [openEmailVerification, setOpenEmailVerification] = useState<boolean>(false);

  const [state, setState] = useState({
    name: "",
    email: "",
    country_code: "1",
    phone_number: "",
    password: "",
    company: "",
    country: "",
    state: "",
    city: "",
    pin_code: "",
    apartment_number: "",
    full_address: "",
    lat: "",
    lng: "",
    loading: false,
  });

  const [documents, setDocuments] = useState<DocumentType[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentConfig[]>([]);

  const fetchDocumentTypes = async () => {
    try {
      const response = await henceforthApi.Auth.getDocumentTypes();
      if (response && response.data && Array.isArray(response.data)) {
        setDocumentTypes(response.data);
      } else {
        setDocumentTypes([
          { type: 'aadhar_card', label: 'Aadhar Card' },
          { type: 'pan_card', label: 'PAN Card' }
        ]);
      }
    } catch (error) {
      setDocumentTypes([
        { type: 'aadhar_card', label: 'Aadhar Card' },
        { type: 'pan_card', label: 'PAN Card' }
      ]);
    }
  };

  useEffect(() => {
    fetchDocumentTypes();
  }, []);

  useEffect(() => {
    if (documentTypes.length > 0) {
      const initialDocuments: DocumentType[] = documentTypes.map((doc) => ({
        type: doc.type,
        front_image: null,
        back_image: null
      }));
      setDocuments(initialDocuments);
    }
  }, [documentTypes]);

  const [store, setStore] = useState({} as any);
  const placeInputRef = useRef<HTMLInputElement | null>(null);
  const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || '';

  // Validation Step 1
  const validateStep1 = (): boolean => {
    let isValid = true;
    if (!state.name.trim()) {
      setNameErr("Full name is required");
      isValid = false;
    }
    if (!state.email.trim()) {
      setEmailErr("Email address is required");
      isValid = false;
    } else if (!validateEmail(state.email)) {
      setEmailErr("Please enter a valid email address");
      isValid = false;
    }
    if (!state.phone_number.trim()) {
      setPhoneErr("Phone number is required");
      isValid = false;
    } else if (state.phone_number.length < 8 || state.phone_number.length > 15) {
      setPhoneErr("Please enter a valid phone number");
      isValid = false;
    }
    if (!state.password.trim()) {
      setPasswordErr("Password is required");
      isValid = false;
    } else if (!strongPasswordValidation(state.password)) {
      setPasswordErr("Min 8 chars, 1 uppercase, 1 lowercase, 1 number & 1 special character");
      isValid = false;
    }
    return isValid;
  };

  // Validation Step 2
  const validateStep2 = (): boolean => {
    let isValid = true;
    if (!state.company.trim()) {
      setCompanyErr("Store or company name is required");
      isValid = false;
    }
    if (!state.full_address.trim()) {
      setAddressErr("Store address is required");
      isValid = false;
    }
    return isValid;
  };

  // Validation Step 3
  const validateStep3 = (): boolean => {
    let isValid = true;
    documents.forEach((doc, index) => {
      if (!doc.front_image) {
        setFrontImageErr(`Please upload front image of ${documentTypes[index]?.label || 'document'}`);
        isValid = false;
      }
      if (!doc.back_image) {
        setBackImageErr(`Please upload back image of ${documentTypes[index]?.label || 'document'}`);
        isValid = false;
      }
    });
    if (!checkbox) {
      setCheckboxErr("You must agree to the Terms & Conditions and Privacy Policy");
      isValid = false;
    }
    return isValid;
  };

  const nextStep = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const submitForm = async (e: any) => {
    e.preventDefault();
    if (!validateStep3()) return;
    setState((prev) => ({ ...prev, loading: true }));

    let fcmid = await requestNotification();
    const { name, email, country_code, phone_number, password, company, country, state: stateVal, city, pin_code, apartment_number, full_address, lat, lng } = state;

    try {
      // Upload all documents first
      const uploadedDocuments = await Promise.all(
        documents.map(async (doc) => {
          let frontKey = '';
          let backKey = '';
          if (doc.front_image) {
            const frontUploadRes = await henceforthApi.Common.do_spaces_file_upload('file', doc.front_image);
            frontKey = frontUploadRes.data.file_name;
          }
          if (doc.back_image) {
            const backUploadRes = await henceforthApi.Common.do_spaces_file_upload('file', doc.back_image);
            backKey = backUploadRes.data.file_name;
          }
          return {
            type: doc.type,
            images: {
              front: frontKey,
              back: backKey
            }
          };
        })
      );

      let items = {
        name,
        email,
        country_code,
        phone_number,
        password,
        company,
        country,
        state: stateVal,
        city,
        pin_code,
        apartment_number,
        full_address,
        lat,
        lng,
        language: "ENGLISH",
        documents: uploadedDocuments
      } as any;

      if (fcmid) items['fcm_token'] = fcmid;

      let res = await henceforthApi.Auth.signup(items);
      setStore(res.data);
      toastMessage("SignUp Successfully");
      setOpenEmailVerification(true);
    } catch (err) {
      handleError(err, 'active', authDispatch);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const changeHandler = (e: any) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const phoneCheck = (e: any, v: any) => {
    setPhoneErr('');
    setState((prev) => ({
      ...prev,
      phone_number: e.phone.slice(Number(v.dialCode.length), Number(e.phone.length)),
      country_code: v.dialCode,
    }));
  };

  henceforthApi.setToken(store.access_token);

  const initialiseProfile = async (data: any) => {
    try {
      let apiRes = await henceforthApi.Auth.emailVerifyOtp({ otp: data, language: 'ENGLISH' });
      loginSuccess({ ...apiRes.data, lang: henceofrthEnums.Language.ENGLISH })(authDispatch);
      henceforthApi.languageChange(henceofrthEnums.Language.ENGLISH);
      setOpenEmailVerification(false);
      navigate(`/`);
    } catch (err) {
      handleError(err, 'active', authDispatch);
    }
  };

  const resendEmailOtp = async () => {
    if (loading) return;
    setLoading(true);
    try {
      let items = { email: state.email, language: "ENGLISH" };
      let apiRes = await henceforthApi.Auth.emailVerifySendOtp(items);
      return apiRes.data;
    } catch (error: any) {
      handleError(error, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  const onCloseModal = (close: boolean) => {
    setOpenEmailVerification(close);
    toastMessage("Please verify your email to continue.");
    navigate('/signin');
  };

  function loadGoogleMapScript(callback: any) {
    if (typeof (window as any).google === "object" && typeof (window as any).google.maps === "object") {
      callback();
    } else {
      const googleMapScript = document.createElement("script");
      googleMapScript.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
      window.document.body.appendChild(googleMapScript);
      googleMapScript.addEventListener("load", callback);
    }
  }

  const initPlaceAPI = () => {
    if (!placeInputRef.current) return;
    loadGoogleMapScript(() => {
      if (placeInputRef.current) {
        let autocomplete = new (window as any).google.maps.places.Autocomplete(
          placeInputRef.current,
          { types: ['address'] }
        );
        autocomplete.addListener("place_changed", () => {
          let place = autocomplete.getPlace();
          let formatAddress = place.formatted_address || '';
          let lat = place.geometry?.location?.lat() || '';
          let lng = place.geometry?.location?.lng() || '';
          const address = place.address_components;
          let items: any = {};
          if (Array.isArray(address)) {
            address.forEach((comp: any) => {
              if (comp.types.includes("postal_code")) items.pin_code = comp.long_name;
              if (comp.types.includes("administrative_area_level_1")) items.state = comp.long_name;
              if (comp.types.includes("locality")) items.city = comp.long_name;
              if (comp.types.includes("country")) items.country = comp.short_name;
              if (comp.types.includes("street_number")) items.apartment_number = comp.long_name;
            });
          }
          setState((prev) => ({
            ...prev,
            country: items.country || "",
            state: items.state || "",
            city: items.city || "",
            pin_code: items.pin_code || "",
            apartment_number: items.apartment_number || "",
            full_address: formatAddress,
            lat: String(lat),
            lng: String(lng),
          }));
          setAddressErr('');
        });
      }
    });
  };

  useEffect(() => {
    if (currentStep === 2 && placeInputRef.current) {
      initPlaceAPI();
    }
  }, [currentStep]);

  const stepsMeta = [
    { number: 1, title: 'Personal Info', icon: User, desc: 'Name & credentials' },
    { number: 2, title: 'Store Details', icon: Building2, desc: 'Company & location' },
    { number: 3, title: 'Verification', icon: FileText, desc: 'KYC & documents' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50/40 to-slate-100 flex items-center justify-center p-4 sm:p-6 lg:p-8 relative overflow-hidden select-none">
      {/* Decorative background blurs */}
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#1AA7DE]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-sky-400/10 blur-3xl pointer-events-none" />

      <div className="w-full max-w-2xl relative z-10 my-4">
        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 p-6 sm:p-10 transition-all">
          {/* Logo & Brand Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block hover:opacity-95 transition-opacity">
              <img
                src={logo}
                alt="Ready Ecommerce"
                className="h-11 w-auto max-w-[210px] object-contain mx-auto"
              />
            </Link>
            <h1 className="mt-2 text-2xl font-black text-slate-800 tracking-tight">
              Create Your Seller Account
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Start selling your products in a few quick steps
            </p>
          </div>

          {/* Stepper Progress Bar */}
          <div className="mb-8">
            <div className="grid grid-cols-3 relative">
              {/* Connecting progress bar track */}
              <div className="absolute top-5 left-1/6 right-1/6 h-0.5 bg-slate-200 -z-0">
                <div
                  className="h-full bg-[#1AA7DE] transition-all duration-300"
                  style={{
                    width: currentStep === 1 ? '0%' : currentStep === 2 ? '50%' : '100%'
                  }}
                />
              </div>

              {stepsMeta.map((s) => {
                const isPassed = currentStep > s.number;
                const isCurrent = currentStep === s.number;
                const StepIcon = s.icon;

                return (
                  <div key={s.number} className="flex flex-col items-center relative z-10">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 shadow-sm ${isPassed
                        ? 'bg-[#1AA7DE] text-white shadow-[#1AA7DE]/30 ring-4 ring-sky-100'
                        : isCurrent
                          ? 'bg-[#1AA7DE] text-white shadow-[#1AA7DE]/30 ring-4 ring-sky-100'
                          : 'bg-white text-slate-400 border-2 border-slate-200'
                        }`}
                    >
                      {isPassed ? <Check className="w-5 h-5 stroke-[2.5]" /> : <StepIcon className="w-4 h-4" />}
                    </div>
                    <span
                      className={`mt-2 text-xs font-bold text-center tracking-tight transition-colors ${isCurrent ? 'text-[#1AA7DE]' : isPassed ? 'text-slate-700' : 'text-slate-400'
                        }`}
                    >
                      {s.title}
                    </span>
                    <span className="text-[10px] text-slate-400 hidden sm:block">
                      {s.desc}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submitForm} noValidate>
            {/* STEP 1: Personal Information */}
            {currentStep === 1 && (
              <div className="space-y-4 animate-fadeIn">
                <div className="border-b border-slate-100 pb-3 mb-4">
                  <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#1AA7DE]" />
                    <span>Personal Details</span>
                  </h3>
                  <p className="text-xs text-slate-500">
                    Your contact information will be used for store notifications
                  </p>
                </div>

                {/* Full Name */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                    Full Name <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <User className="w-5 h-5" />
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={state.name}
                      maxLength={40}
                      onChange={(e) => {
                        setNameErr('');
                        changeHandler(e);
                      }}
                      placeholder="e.g. John Doe"
                      className={`w-full pl-11 pr-4 py-3 bg-slate-50/70 border ${nameErr ? 'border-rose-400 bg-rose-50/30 ring-2 ring-rose-100' : 'border-slate-200 hover:border-slate-300'
                        } rounded-xl text-slate-800 placeholder-slate-400 text-sm font-medium focus:bg-white focus:border-[#1AA7DE] focus:ring-4 focus:ring-[#1AA7DE]/10 outline-none transition-all`}
                    />
                  </div>
                  {nameErr && (
                    <p className="mt-1.5 text-xs font-semibold text-rose-500 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>{nameErr}</span>
                    </p>
                  )}
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                    Email Address <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <Mail className="w-5 h-5" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={state.email}
                      onChange={(e) => {
                        setEmailErr('');
                        changeHandler(e);
                      }}
                      placeholder="seller@example.com"
                      className={`w-full pl-11 pr-4 py-3 bg-slate-50/70 border ${emailErr ? 'border-rose-400 bg-rose-50/30 ring-2 ring-rose-100' : 'border-slate-200 hover:border-slate-300'
                        } rounded-xl text-slate-800 placeholder-slate-400 text-sm font-medium focus:bg-white focus:border-[#1AA7DE] focus:ring-4 focus:ring-[#1AA7DE]/10 outline-none transition-all`}
                    />
                  </div>
                  {emailErr && (
                    <p className="mt-1.5 text-xs font-semibold text-rose-500 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>{emailErr}</span>
                    </p>
                  )}
                </div>

                {/* Phone Number with react-phone-input-2 */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                    Phone Number <span className="text-rose-500">*</span>
                  </label>
                  <div className={`seller-phone-wrap ${phoneErr ? 'ring-2 ring-rose-200 rounded-xl' : ''}`}>
                    <PhoneInput
                      country={'us'}
                      value={state.country_code + state.phone_number}
                      onChange={(phone: any, value: any) => phoneCheck({ phone }, value)}
                      inputClass="!w-full !h-12 !pl-14 !pr-4 !bg-slate-50/70 !border !border-slate-200 !rounded-xl !text-slate-800 !text-sm !font-medium focus:!bg-white focus:!border-[#1AA7DE] focus:!ring-4 focus:!ring-[#1AA7DE]/10 !outline-none !transition-all"
                      buttonClass="!bg-slate-50/70 !border-slate-200 !rounded-l-xl !px-2.5 hover:!bg-slate-100"
                      containerClass="!w-full"
                    />
                  </div>
                  {phoneErr && (
                    <p className="mt-1.5 text-xs font-semibold text-rose-500 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>{phoneErr}</span>
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                    Password <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <Lock className="w-5 h-5" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={state.password}
                      onChange={(e) => {
                        setPasswordErr('');
                        changeHandler(e);
                      }}
                      placeholder="••••••••"
                      className={`w-full pl-11 pr-11 py-3 bg-slate-50/70 border ${passwordErr ? 'border-rose-400 bg-rose-50/30 ring-2 ring-rose-100' : 'border-slate-200 hover:border-slate-300'
                        } rounded-xl text-slate-800 placeholder-slate-400 text-sm font-medium focus:bg-white focus:border-[#1AA7DE] focus:ring-4 focus:ring-[#1AA7DE]/10 outline-none transition-all`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {passwordErr ? (
                    <p className="mt-1.5 text-xs font-semibold text-rose-500 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>{passwordErr}</span>
                    </p>
                  ) : (
                    <p className="mt-1 text-[11px] text-slate-400">
                      Must contain min 8 chars with uppercase, lowercase, number and symbol
                    </p>
                  )}
                </div>

                {/* Step 1 Continue Button */}
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={nextStep}
                    className="w-full py-3.5 px-4 bg-[#1AA7DE] hover:bg-[#1596c9] active:scale-[0.99] text-white font-bold rounded-xl shadow-lg shadow-[#1AA7DE]/25 hover:shadow-xl hover:shadow-[#1AA7DE]/30 transition-all flex items-center justify-center gap-2 cursor-pointer group text-base"
                  >
                    <span>Continue to Store Details</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: Company / Business Information */}
            {currentStep === 2 && (
              <div className="space-y-4 animate-fadeIn">
                <div className="border-b border-slate-100 pb-3 mb-4">
                  <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#1AA7DE]" />
                    <span>Store & Business Information</span>
                  </h3>
                  <p className="text-xs text-slate-500">
                    Tell us about your brand and business location
                  </p>
                </div>

                {/* Company / Store Name */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                    Store / Company Name <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <input
                      type="text"
                      name="company"
                      value={state.company}
                      maxLength={60}
                      onChange={(e) => {
                        setCompanyErr('');
                        changeHandler(e);
                      }}
                      placeholder="e.g. Acme Retailers"
                      className={`w-full pl-11 pr-4 py-3 bg-slate-50/70 border ${companyErr ? 'border-rose-400 bg-rose-50/30 ring-2 ring-rose-100' : 'border-slate-200 hover:border-slate-300'
                        } rounded-xl text-slate-800 placeholder-slate-400 text-sm font-medium focus:bg-white focus:border-[#1AA7DE] focus:ring-4 focus:ring-[#1AA7DE]/10 outline-none transition-all`}
                    />
                  </div>
                  {companyErr && (
                    <p className="mt-1.5 text-xs font-semibold text-rose-500 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>{companyErr}</span>
                    </p>
                  )}
                </div>

                {/* Store Full Address (Google Place Autocomplete) */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                    Store Address <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <input
                      ref={placeInputRef}
                      type="text"
                      name="full_address"
                      value={state.full_address}
                      maxLength={200}
                      onChange={(e) => {
                        setAddressErr('');
                        changeHandler(e);
                      }}
                      placeholder="Start typing your street address..."
                      className={`w-full pl-11 pr-4 py-3 bg-slate-50/70 border ${addressErr ? 'border-rose-400 bg-rose-50/30 ring-2 ring-rose-100' : 'border-slate-200 hover:border-slate-300'
                        } rounded-xl text-slate-800 placeholder-slate-400 text-sm font-medium focus:bg-white focus:border-[#1AA7DE] focus:ring-4 focus:ring-[#1AA7DE]/10 outline-none transition-all`}
                    />
                  </div>
                  {addressErr && (
                    <p className="mt-1.5 text-xs font-semibold text-rose-500 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>{addressErr}</span>
                    </p>
                  )}
                </div>

                {/* Auto-detected address tags */}
                {(state.city || state.state || state.pin_code || state.country) && (
                  <div className="p-3.5 rounded-xl bg-sky-50/60 border border-sky-100 space-y-1">
                    <p className="text-xs font-bold text-sky-800 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-[#1AA7DE]" />
                      <span>Detected Location Details</span>
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs font-medium text-slate-700 pt-1">
                      {state.city && <span className="px-2 py-0.5 bg-white rounded-md border border-sky-200">City: <strong>{state.city}</strong></span>}
                      {state.state && <span className="px-2 py-0.5 bg-white rounded-md border border-sky-200">State: <strong>{state.state}</strong></span>}
                      {state.pin_code && <span className="px-2 py-0.5 bg-white rounded-md border border-sky-200">PIN: <strong>{state.pin_code}</strong></span>}
                      {state.country && <span className="px-2 py-0.5 bg-white rounded-md border border-sky-200">Country: <strong>{state.country}</strong></span>}
                    </div>
                  </div>
                )}

                {/* Step 2 Action Buttons */}
                <div className="flex items-center gap-3 pt-4">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="py-3 px-5 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer text-sm"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex-1 py-3.5 px-4 bg-[#1AA7DE] hover:bg-[#1596c9] active:scale-[0.99] text-white font-bold rounded-xl shadow-lg shadow-[#1AA7DE]/25 hover:shadow-xl hover:shadow-[#1AA7DE]/30 transition-all flex items-center justify-center gap-2 cursor-pointer group text-base"
                  >
                    <span>Continue to Documents</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: Document Uploads & Terms Agreement */}
            {currentStep === 3 && (
              <div className="space-y-5 animate-fadeIn">
                <div className="border-b border-slate-100 pb-3 mb-2">
                  <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#1AA7DE]" />
                    <span>KYC & Document Verification</span>
                  </h3>
                  <p className="text-xs text-slate-500">
                    Upload official government identification documents to verify your business
                  </p>
                </div>

                {/* Document Upload Cards */}
                {documents.map((doc, index) => {
                  const docLabel = documentTypes[index]?.label || 'Identification Document';
                  return (
                    <div key={doc.type || index} className="p-4 rounded-2xl bg-slate-50/70 border border-slate-200/80 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                          <FileText className="w-4 h-4 text-[#1AA7DE]" />
                          <span>{docLabel}</span>
                        </span>
                        <span className="text-[11px] font-semibold text-slate-400">JPG, PNG</span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Front Image Upload */}
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1">
                            Front Side Image <span className="text-rose-500">*</span>
                          </label>
                          <label className={`relative flex flex-col items-center justify-center p-3.5 border-2 border-dashed rounded-xl cursor-pointer transition-all ${doc.front_image
                            ? 'border-emerald-300 bg-emerald-50/40'
                            : 'border-slate-200 hover:border-[#1AA7DE] bg-white hover:bg-sky-50/30'
                            }`}>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e: any) => {
                                setFrontImageErr('');
                                const file = e.target.files?.[0] || null;
                                const updatedDocs = [...documents];
                                updatedDocs[index].front_image = file;
                                setDocuments(updatedDocs);
                              }}
                            />
                            {doc.front_image ? (
                              <div className="flex items-center gap-2.5 w-full">
                                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-bold text-slate-800 truncate">
                                    {doc.front_image.name}
                                  </p>
                                  <p className="text-[10px] text-emerald-600 font-semibold">
                                    Front side ready
                                  </p>
                                </div>
                                <span className="text-xs font-semibold text-[#1AA7DE] hover:underline">
                                  Change
                                </span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center text-center py-1">
                                <UploadCloud className="w-6 h-6 text-slate-400 mb-1" />
                                <span className="text-xs font-semibold text-slate-700">
                                  Upload Front Side
                                </span>
                                <span className="text-[10px] text-slate-400">
                                  Click to browse image
                                </span>
                              </div>
                            )}
                          </label>
                        </div>

                        {/* Back Image Upload */}
                        <div>
                          <label className="block text-xs font-bold text-slate-600 mb-1">
                            Back Side Image <span className="text-rose-500">*</span>
                          </label>
                          <label className={`relative flex flex-col items-center justify-center p-3.5 border-2 border-dashed rounded-xl cursor-pointer transition-all ${doc.back_image
                            ? 'border-emerald-300 bg-emerald-50/40'
                            : 'border-slate-200 hover:border-[#1AA7DE] bg-white hover:bg-sky-50/30'
                            }`}>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e: any) => {
                                setBackImageErr('');
                                const file = e.target.files?.[0] || null;
                                const updatedDocs = [...documents];
                                updatedDocs[index].back_image = file;
                                setDocuments(updatedDocs);
                              }}
                            />
                            {doc.back_image ? (
                              <div className="flex items-center gap-2.5 w-full">
                                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-bold text-slate-800 truncate">
                                    {doc.back_image.name}
                                  </p>
                                  <p className="text-[10px] text-emerald-600 font-semibold">
                                    Back side ready
                                  </p>
                                </div>
                                <span className="text-xs font-semibold text-[#1AA7DE] hover:underline">
                                  Change
                                </span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center text-center py-1">
                                <UploadCloud className="w-6 h-6 text-slate-400 mb-1" />
                                <span className="text-xs font-semibold text-slate-700">
                                  Upload Back Side
                                </span>
                                <span className="text-[10px] text-slate-400">
                                  Click to browse image
                                </span>
                              </div>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {(frontImageErr || backImageErr) && (
                  <p className="text-xs font-semibold text-rose-500 flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{frontImageErr || backImageErr}</span>
                  </p>
                )}

                {/* Terms and Conditions Checkbox */}
                <div className="pt-2">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={checkbox}
                      onChange={(e) => {
                        setCheckbox(e.target.checked);
                        setCheckboxErr('');
                      }}
                      className="mt-1 w-4 h-4 text-[#1AA7DE] rounded border-slate-300 focus:ring-[#1AA7DE]"
                    />
                    <span className="text-xs font-medium text-slate-600 leading-relaxed">
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" className="font-bold text-[#1AA7DE] hover:underline">
                        Terms & Conditions
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" target="_blank" className="font-bold text-[#1AA7DE] hover:underline">
                        Privacy Policy
                      </Link>
                      . I declare that the business information provided is true and accurate.
                    </span>
                  </label>
                  {checkboxErr && (
                    <p className="mt-1.5 text-xs font-semibold text-rose-500 flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>{checkboxErr}</span>
                    </p>
                  )}
                </div>

                {/* Step 3 Action Buttons */}
                <div className="flex items-center gap-3 pt-4">
                  <button
                    type="button"
                    onClick={prevStep}
                    disabled={state.loading}
                    className="py-3 px-5 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer text-sm disabled:opacity-50"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <button
                    type="submit"
                    disabled={state.loading}
                    className="flex-1 py-3.5 px-4 bg-[#1AA7DE] hover:bg-[#1596c9] active:scale-[0.99] text-white font-bold rounded-xl shadow-lg shadow-[#1AA7DE]/25 hover:shadow-xl hover:shadow-[#1AA7DE]/30 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed group text-base"
                  >
                    {state.loading ? (
                      <Spinner color="text-white" />
                    ) : (
                      <>
                        <span>Complete Registration</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* Switch to Sign In */}
          <div className="mt-8 pt-6 border-t border-slate-100 text-center">
            <p className="text-sm font-medium text-slate-500">
              Already have a seller account?{' '}
              <Link
                to="/signin"
                className="font-bold text-[#1AA7DE] hover:text-[#1596c9] hover:underline transition-colors ml-1"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Email Verification OTP Modal */}
      {openEmailVerification && (
        <VerifyOtp
          verifyCallback={initialiseProfile}
          resendOtpCallback={resendEmailOtp}
          onCloseModal={onCloseModal}
        />
      )}
    </div>
  );
};

export default Signup;
